import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LifeBuoy, KeyRound, MailCheck, Unlock, ShieldOff, UserCog, Smartphone, Trash2, Copy, ShieldAlert, ChevronDown, ChevronUp } from 'lucide-react';
import { startImpersonation } from '../../utils/impersonation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const cardStyle = {
  background: '#FFFFFF',
  border: '1px solid rgba(220, 38, 38, 0.15)',
  borderRadius: 18,
  padding: 16,
};

const sectionTitleStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 12,
};

const buttonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: '#FFFFFF',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 12,
  padding: '10px 12px',
  fontSize: 13,
  fontWeight: 600,
  color: '#222',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
  transition: 'background 150ms ease',
};

const dangerButtonStyle = {
  ...buttonStyle,
  background: '#FFF1F1',
  border: '1px solid #FBD3D3',
  color: '#B91C1C',
};

const impersonateButtonStyle = {
  ...buttonStyle,
  background: 'var(--accent-purple-strong)',
  border: 'none',
  color: '#FFFFFF',
  justifyContent: 'center',
  fontWeight: 700,
  padding: '12px 14px',
  fontSize: 14,
};

const formatRelative = (iso) => {
  if (!iso) return '—';
  try {
    const dt = new Date(iso);
    const diffMs = Date.now() - dt.getTime();
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  } catch {
    return iso;
  }
};

export const AdminUserSupportActions = ({ userId, userEmail, isAdmin }) => {
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [devicesExpanded, setDevicesExpanded] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const adminUser = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchDevices = useCallback(async () => {
    if (!userId) return;
    setLoadingDevices(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/trusted-devices`, { headers });
      setDevices(res.data || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('failed to load trusted devices', err);
      toast.error('Failed to load devices');
    } finally {
      setLoadingDevices(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const runAction = useCallback(async (key, label, fn) => {
    setBusyAction(key);
    try {
      await fn();
      toast.success(label);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed: ${label}`);
    } finally {
      setBusyAction(null);
    }
  }, []);

  const handleImpersonate = useCallback(async () => {
    if (isAdmin) {
      toast.error('Cannot impersonate another admin.');
      return;
    }
    if (!window.confirm(`Impersonate ${userEmail}? You will see the app as them in this tab.`)) return;
    setBusyAction('impersonate');
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/impersonate`, {}, { headers });
      const { access_token, user, ttl_hours } = res.data;
      startImpersonation({
        adminToken: token,
        adminUser,
        targetToken: access_token,
        targetUser: user,
        ttlHours: ttl_hours || 1,
      });
      toast.success(`Impersonating ${user.email || user.name}`);
      // Hard reload so every cached header/state picks up the new identity
      window.location.assign('/');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to impersonate');
      setBusyAction(null);
    }
  }, [isAdmin, userId, userEmail, token, adminUser, headers]);

  const handleSendReset = () =>
    runAction('reset', 'Password reset email sent', async () => {
      await axios.post(`${API}/admin/users/${userId}/account-recovery/send-reset`, {}, { headers });
    });

  const handleResendVerification = () =>
    runAction('verify', 'Verification email sent', async () => {
      const res = await axios.post(`${API}/admin/users/${userId}/account-recovery/resend-verification`, {}, { headers });
      if (res.data?.resent === false) {
        throw Object.assign(new Error('Email already verified'), { response: { data: { detail: 'Email already verified' } } });
      }
    });

  const handleUnlock = () =>
    runAction('unlock', 'Account unlocked', async () => {
      await axios.post(`${API}/admin/users/${userId}/account-recovery/unlock`, {}, { headers });
    });

  const handleDisable2FA = () => {
    if (!window.confirm('Disable 2FA for this user? They will be able to log in with password only.')) return;
    runAction('disable-2fa', '2FA disabled', async () => {
      await axios.post(`${API}/admin/users/${userId}/account-recovery/disable-2fa`, {}, { headers });
    });
  };

  const handleSetTempPassword = () => {
    if (!window.confirm('Generate a temporary password? The user\'s current password will be overwritten.')) return;
    setBusyAction('temp-pwd');
    axios
      .post(`${API}/admin/users/${userId}/account-recovery/set-temp-password`, {}, { headers })
      .then((res) => {
        setTempPassword(res.data?.temp_password || '');
        toast.success('Temporary password generated');
      })
      .catch((err) => toast.error(err?.response?.data?.detail || 'Failed to set temp password'))
      .finally(() => setBusyAction(null));
  };

  const handleCopyTemp = () => {
    if (!tempPassword) return;
    try {
      navigator.clipboard.writeText(tempPassword);
      toast.success('Temp password copied');
    } catch {
      toast.error('Copy failed — select & copy manually');
    }
  };

  const handleRevokeDevice = (fingerprint) => {
    if (!window.confirm('Revoke this device? It will lose trusted 2FA status.')) return;
    runAction(`revoke-${fingerprint}`, 'Device revoked', async () => {
      await axios.delete(`${API}/admin/users/${userId}/trusted-devices/${fingerprint}`, { headers });
      await fetchDevices();
    });
  };

  const handleRevokeAll = () => {
    if (!window.confirm('Revoke all devices for this user? Next login will require fresh OTP verification.')) return;
    runAction('revoke-all', 'All devices revoked', async () => {
      await axios.delete(`${API}/admin/users/${userId}/trusted-devices`, { headers });
      await fetchDevices();
    });
  };

  return (
    <div data-testid="admin-user-support-actions" style={{ display: 'grid', gap: 12 }}>
      {/* Impersonate */}
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>
          <UserCog size={20} color="var(--accent-purple-strong)" />
          <h3 style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, margin: 0 }}>Impersonate</h3>
        </div>
        <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px' }}>
          View the app as this user to debug a support case. All actions during impersonation are audit-logged.
        </p>
        <button
          type="button"
          style={{ ...impersonateButtonStyle, opacity: isAdmin ? 0.5 : 1, cursor: isAdmin ? 'not-allowed' : 'pointer' }}
          disabled={!!busyAction || isAdmin}
          onClick={handleImpersonate}
          data-testid="admin-impersonate-button"
        >
          <UserCog size={16} /> {busyAction === 'impersonate' ? 'Opening…' : `Impersonate ${userEmail || 'user'}`}
        </button>
        {isAdmin && (
          <p style={{ fontSize: 11, color: '#B91C1C', marginTop: 6 }}>
            <ShieldAlert size={12} style={{ verticalAlign: 'middle' }} /> Cannot impersonate another admin.
          </p>
        )}
      </div>

      {/* Account recovery */}
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>
          <LifeBuoy size={20} color="var(--accent-purple-strong)" />
          <h3 style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, margin: 0 }}>Account recovery</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
          <button type="button" style={buttonStyle} disabled={!!busyAction} onClick={handleSendReset} data-testid="admin-send-reset-button">
            <KeyRound size={14} /> Send password reset
          </button>
          <button type="button" style={buttonStyle} disabled={!!busyAction} onClick={handleResendVerification} data-testid="admin-resend-verification-button">
            <MailCheck size={14} /> Resend verification
          </button>
          <button type="button" style={buttonStyle} disabled={!!busyAction} onClick={handleUnlock} data-testid="admin-unlock-account-button">
            <Unlock size={14} /> Unlock account
          </button>
          <button type="button" style={dangerButtonStyle} disabled={!!busyAction} onClick={handleDisable2FA} data-testid="admin-disable-2fa-button">
            <ShieldOff size={14} /> Disable 2FA
          </button>
          <button type="button" style={dangerButtonStyle} disabled={!!busyAction} onClick={handleSetTempPassword} data-testid="admin-set-temp-password-button">
            <KeyRound size={14} /> Set temp password
          </button>
        </div>
        {tempPassword && (
          <div
            data-testid="admin-temp-password-display"
            style={{
              marginTop: 10,
              padding: 10,
              borderRadius: 12,
              background: '#FFF9DB',
              border: '1px solid #FCE588',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span style={{ fontSize: 11, color: '#8A6D00', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>Temp password</span>
            <code style={{ flex: 1, fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: '#222', wordBreak: 'break-all' }}>{tempPassword}</code>
            <button
              type="button"
              style={{ ...buttonStyle, width: 'auto', padding: '6px 10px' }}
              onClick={handleCopyTemp}
              data-testid="admin-temp-password-copy"
            >
              <Copy size={12} /> Copy
            </button>
          </div>
        )}
      </div>

      {/* Trusted devices */}
      <div style={cardStyle}>
        <div style={{ ...sectionTitleStyle, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Smartphone size={20} color="var(--accent-purple-strong)" />
            <h3 style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, margin: 0 }}>
              Trusted devices ({devices.length})
            </h3>
          </div>
          {devices.length > 0 && (
            <button
              type="button"
              style={{ ...dangerButtonStyle, width: 'auto', padding: '6px 12px' }}
              disabled={!!busyAction}
              onClick={handleRevokeAll}
              data-testid="admin-revoke-all-devices-button"
            >
              <Trash2 size={12} /> Revoke all
            </button>
          )}
        </div>
        {loadingDevices ? (
          <p style={{ textAlign: 'center', color: '#999', fontSize: 12, padding: 12 }}>Loading devices…</p>
        ) : devices.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#999', fontSize: 12, padding: 12 }}>No devices seen yet.</p>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {(devicesExpanded ? devices : devices.slice(0, 1)).map((device) => (
              <div
                key={device.fingerprint}
                data-testid={`admin-trusted-device-${device.fingerprint}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: 10,
                  background: '#FAFAFA',
                  borderRadius: 12,
                  border: '1px solid #EEE',
                }}
              >
                <Smartphone size={18} color={device.is_trusted_2fa ? '#00B341' : '#999'} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111' }}>
                    {device.ua_summary || 'Unknown device'}
                    {device.is_trusted_2fa && (
                      <span style={{ marginLeft: 6, fontSize: 10, padding: '2px 6px', borderRadius: 999, background: '#E5FBEB', color: '#00803A', fontWeight: 700 }}>
                        2FA trusted
                      </span>
                    )}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: '#777' }}>
                    Last seen {formatRelative(device.last_seen_at)} · IP {device.ip || '—'}
                  </p>
                </div>
                <button
                  type="button"
                  style={{ ...dangerButtonStyle, width: 'auto', padding: '6px 10px' }}
                  disabled={!!busyAction}
                  onClick={() => handleRevokeDevice(device.fingerprint)}
                  data-testid={`admin-revoke-device-${device.fingerprint}`}
                >
                  <Trash2 size={12} /> Revoke
                </button>
              </div>
            ))}
            {devices.length > 1 ? (
              <button
                type="button"
                onClick={() => setDevicesExpanded((v) => !v)}
                data-testid="admin-trusted-devices-toggle"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  marginTop: 4,
                  padding: '8px 12px',
                  borderRadius: 10,
                  border: '1px solid #E5E7EB',
                  background: '#FFFFFF',
                  color: '#3B404B',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 12.5,
                }}
              >
                {devicesExpanded
                  ? <>Hide {devices.length - 1} more <ChevronUp size={14} /></>
                  : <>Show all {devices.length} devices <ChevronDown size={14} /></>}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserSupportActions;
